import { Alert, TextField } from "@mui/material";
import * as React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import {
  PageBreadcrumbs,
  PageLayout,
  RobotoButton,
  RobotoLoadingButton,
} from "@/shared/components";
import { FilePicker } from "@/shared/components/FilePicker";
import { SessionCompositionStatus } from "@/shared/components/sessions/SessionCompositionStatus";
import { type SessionCreationResult } from "@/shared/domain/sessions";
import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation/Goto";
import { type CreateSessionLocationState } from "@/shared/navigation/types";

import styles from "./CreateSessionPage.module.css";
import { DevicesSection } from "./DevicesSection";
import { FilesSection } from "./FilesSection";
import { SessionCustomFieldsSection } from "./SessionCustomFieldsSection";
import {
  useCreateSessionForm,
  type CreateSessionForm,
} from "./useCreateSessionForm";

/**
 * Full-page create-session form at `/sessions/create`. Reachable from the
 * top-level Sessions page, the Sessions tab in dataset details, and a
 * dataset's file-browser selection menu — dataset-scoped entry points seed
 * the file selection via the `dataset_id` query param and navigation state.
 */
export function CreateSessionPage() {
  const orgId = useCurrentOrgId();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  // Normalize both a missing param and the malformed empty value
  // (`?dataset_id=`) to undefined, so every downstream "dataset in scope"
  // guard agrees on one definition.
  const datasetId = searchParams.get("dataset_id") || undefined;

  const location = useLocation();
  const locationState = location.state as CreateSessionLocationState | null;

  const form = useCreateSessionForm({
    orgId,
    pageKey: location.key,
    datasetId,
    fileIds: locationState?.fileIds,
    directoryPatterns: locationState?.directoryPatterns,
  });

  const [filePickerOpen, setFilePickerOpen] = React.useState(false);

  const handleCancel = () => {
    form.stop();
    // On a direct entry (deep link, refresh, new tab) there is no in-app
    // history entry behind this page — `navigate(-1)` would no-op or leave
    // the SPA — so fall back to the Sessions list.
    if (location.key === "default") {
      void navigate(Goto.route.Sessions);
    } else {
      void navigate(-1);
    }
  };

  return (
    <PageLayout
      title="Roboto - Create session"
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Sessions}>Sessions</Link>
          <span>Create session</span>
        </PageBreadcrumbs>
      }
      footer={
        <div className={styles.footerActions}>
          <RobotoButton
            variant="text"
            size="large"
            eventName="CreateSessionCanceled"
            onClick={handleCancel}
          >
            Cancel
          </RobotoButton>
          <RobotoLoadingButton
            variant="contained"
            size="large"
            loading={form.isSubmitting}
            disabled={!form.canSubmit}
            eventName="CreateSessionSubmitted"
            eventProperties={{
              datasetId,
              fileCount: form.selectedFiles.length,
            }}
            onClick={() => {
              void form.submit();
            }}
          >
            Create session
          </RobotoLoadingButton>
        </div>
      }
    >
      <div className={styles.container}>
        <DatasetSubhead datasetId={datasetId} />

        <section className={styles.section}>
          <TextField
            id="session-name"
            label="Name"
            value={form.name}
            fullWidth
            size="small"
            // Mirrors the API's 120-character cap on session names.
            inputProps={{ maxLength: 120 }}
            placeholder="Optional — e.g. Morning test drive"
            helperText="You can leave this blank and rename the session later."
            onChange={(event) => form.setName(event.target.value)}
          />
        </section>

        <FilesSection
          files={form.selectedFiles}
          isLoadingFiles={form.isPrefilling}
          filesLoadError={form.prefillError}
          onAddFiles={() => setFilePickerOpen(true)}
          onRemoveFiles={form.removeFiles}
        />

        <DevicesSection
          orgId={orgId}
          inferredDeviceIds={form.inferredDeviceIds}
          isInferringDevices={form.isInferringDevices}
          inferDevicesError={form.inferDevicesError}
          explicitDeviceIds={form.explicitDeviceIds}
          onAddDevice={form.addDevice}
          onRemoveDevice={form.removeDevice}
        />

        <SessionCustomFieldsSection
          orgId={orgId}
          values={form.customFieldValues}
          onChange={form.setCustomFieldValues}
        />

        <SubmitErrorBanner message={form.submitError} />
        <CreationRecovery form={form} />
      </div>

      <FilePicker
        orgId={orgId}
        open={filePickerOpen}
        onClose={() => setFilePickerOpen(false)}
        onSubmit={form.mergeFiles}
        title="Add files to session"
        activeDatasetIds={datasetId ? [datasetId] : undefined}
        existingFileIds={form.selectedFiles.map((file) => file.file_id)}
      />
    </PageLayout>
  );
}

function DatasetSubhead({ datasetId }: { datasetId: string | undefined }) {
  if (datasetId === undefined) {
    return null;
  }
  return (
    <p className={styles.muted}>
      Creating from dataset{" "}
      <Link to={Goto.createDatasetRoute(datasetId)}>{datasetId}</Link>
    </p>
  );
}

function SubmitErrorBanner({ message }: { message: React.ReactNode }) {
  // Cover every value React renders as nothing — not just `null` — so an
  // empty message can't paint an empty error box.
  if (
    message === null ||
    message === undefined ||
    message === false ||
    message === true ||
    message === ""
  ) {
    return null;
  }
  return <Alert severity="error">{message}</Alert>;
}

/**
 * Results and recovery actions for one creation attempt, from the moment
 * submit prepares the composition until the user discards the results.
 * Every state leaves the user something to act on: stop an attempt still adding
 * files, check whether a session the client could not delete is still there,
 * open the session, refresh the sessions list, or discard the results.
 */
function CreationRecovery({ form }: { form: CreateSessionForm }) {
  if (!form.outcome && !form.creationResult) {
    return null;
  }
  const busy = form.isSubmitting || form.isRecovering;
  return (
    <section className={styles.section} aria-label="Session creation results">
      <p role="status">
        {creationMessage(form.creationResult, form.isSubmitting)}
      </p>
      <SessionCompositionStatus outcome={form.outcome} />
      <div className={styles.recoveryActions}>
        <StopCreationButton form={form} />
        <CheckSessionButton form={form} busy={busy} />
        <ViewSessionLink result={form.creationResult} />
        <SettledCreationActions form={form} busy={busy} />
      </div>
    </section>
  );
}

function StopCreationButton({ form }: { form: CreateSessionForm }) {
  if (!form.isSubmitting) {
    return null;
  }
  return (
    <Button variant="outlined" onClick={form.stop}>
      Stop adding files
    </Button>
  );
}

function CheckSessionButton({
  form,
  busy,
}: {
  form: CreateSessionForm;
  busy: boolean;
}) {
  if (form.creationResult?.kind !== "cleanupUnknown") {
    return null;
  }
  return (
    <Button
      variant="outlined"
      // The check reads back the session this page's attempt created, so it
      // is offered only while this page still holds that attempt's context.
      disabled={busy || !form.contextMatches}
      onClick={() => {
        void form.checkCreation();
      }}
    >
      Check session
    </Button>
  );
}

function ViewSessionLink({
  result,
}: {
  result: SessionCreationResult | undefined;
}) {
  if (result?.kind !== "completed" && result?.kind !== "cleanupUnknown") {
    return null;
  }
  return (
    <Link to={Goto.createSessionRoute(result.sessionId)}>View session</Link>
  );
}

function SettledCreationActions({
  form,
  busy,
}: {
  form: CreateSessionForm;
  busy: boolean;
}) {
  // Settled covers both an attempt that returned a result and one stopped
  // after its composition was prepared but before the create was dispatched.
  const settled =
    form.creationResult !== undefined ||
    (form.outcome !== undefined && !form.isSubmitting);
  if (!settled) {
    return null;
  }
  return (
    <>
      <Button
        variant="outlined"
        disabled={busy}
        onClick={() => {
          void form.refreshSessions();
        }}
      >
        Refresh sessions
      </Button>
      <Button variant="text" disabled={busy} onClick={form.prepareNewCreation}>
        Discard results
      </Button>
    </>
  );
}

/**
 * What the attempt left behind, in the user's terms: whether a session exists,
 * whether anything was changed, and what they should confirm before creating
 * another one. Without a result the attempt is either still running or was
 * stopped before the create request went out.
 */
function creationMessage(
  result: SessionCreationResult | undefined,
  isSubmitting: boolean,
): string {
  if (!result) {
    return isSubmitting
      ? "Creating the session and adding its files."
      : "The session was not created. Nothing was changed.";
  }
  switch (result.kind) {
    case "completed":
      return "Session created.";
    case "creationUnknown":
      return "Could not confirm that the session was created, so one may already exist. Check your sessions before creating another.";
    case "compensated":
      return "Not every file was added, so the new session was deleted.";
    case "cleanupUnknown":
      if (result.checkError !== undefined) {
        return "Not every file was added. Could not confirm whether the session was deleted or still exists.";
      }
      return result.survivingSession
        ? "Not every file was added, and deleting the session failed. The session still exists; review it before creating another."
        : "Not every file was added, and deleting the session could not be confirmed.";
  }
}
